import type { ReactNode } from 'react'

import { Avatar } from '@/components/ui/Avatar'
import { CompletionBar } from '@/components/ui/CompletionBar'
import { formatCurrency, formatMonthsAsYears, formatRelativeTime } from '@/utils/format'

interface ProfileCardProps {
  name: string
  // "Job Portal (native)" or "{lms name} · {academic phase}" — every
  // source+phase combination for the same real person is its own
  // separate card, never merged; this badge is how a recruiter tells
  // them apart.
  program?: string | null
  headline?: string | null
  photoUrl?: string | null
  currentCity?: string | null
  currentState?: string | null
  currentCompany?: string | null
  currentDesignation?: string | null
  totalExperienceMonths?: number | null
  isFresher?: boolean
  expectedCtc?: number | null
  noticePeriodDays?: number | null
  topSkills?: string[]
  // Skill names to render highlighted (bold, accent color) — the subset of
  // topSkills that actually matched the recruiter's current search filters.
  // No AI involved: purely a set-intersection the caller already computed.
  matchedSkills?: string[]
  topEducations?: string[]
  preferredLocations?: string[]
  profileCompletionPercent?: number
  hasResume?: boolean
  updatedAt?: string | null
  onViewDetail?: () => void
  onScheduleReminder?: () => void
  actions?: ReactNode
  extra?: ReactNode
  hoverContent?: ReactNode
  selected?: boolean
  onToggleSelect?: () => void
}

/**
 * One candidate per row (Naukri Resdex list layout, not a card grid) —
 * details on the left (grows to fill the row), photo/summary/actions in a
 * fixed-width right column, and a slim icon rail (reminder, etc.) at the
 * far right edge.
 */
export function ProfileCard({
  name,
  program,
  headline,
  photoUrl,
  currentCity,
  currentState,
  currentCompany,
  currentDesignation,
  totalExperienceMonths,
  isFresher,
  expectedCtc,
  noticePeriodDays,
  topSkills = [],
  matchedSkills = [],
  topEducations = [],
  preferredLocations = [],
  profileCompletionPercent,
  hasResume,
  updatedAt,
  onViewDetail,
  onScheduleReminder,
  actions,
  extra,
  hoverContent,
  selected,
  onToggleSelect,
}: ProfileCardProps) {
  const location = [currentCity, currentState].filter(Boolean).join(', ')
  const matchedSet = new Set(matchedSkills)

  return (
    <div
      className={`card custom-card position-relative ${selected ? 'border-primary' : ''}`.trim()}
      style={{ overflow: 'visible' }}
    >
      {hoverContent && (
        <div
          className="position-absolute top-0 start-0 bg-dark text-white rounded p-2 fs-11 shadow"
          style={{
            width: 260,
            zIndex: 20,
            transform: 'translateY(-100%)',
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.15s',
          }}
          data-hover-panel
        >
          {hoverContent}
        </div>
      )}
      <div
        className="card-body"
        onMouseEnter={(e) => {
          const panel = e.currentTarget.parentElement?.querySelector<HTMLElement>('[data-hover-panel]')
          if (panel) {
            panel.style.opacity = '1'
            panel.style.visibility = 'visible'
          }
        }}
        onMouseLeave={(e) => {
          const panel = e.currentTarget.parentElement?.querySelector<HTMLElement>('[data-hover-panel]')
          if (panel) {
            panel.style.opacity = '0'
            panel.style.visibility = 'hidden'
          }
        }}
      >
        <div className="d-flex flex-column flex-lg-row gap-3">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex align-items-start gap-2 mb-2">
              {onToggleSelect && (
                <input
                  type="checkbox"
                  className="form-check-input mt-1"
                  checked={!!selected}
                  onChange={onToggleSelect}
                  aria-label={`Select ${name}`}
                />
              )}
              <div>
                <div className="d-flex align-items-center gap-2">
                  <p className="fw-semibold fs-16 mb-0">{name}</p>
                  {program && <span className="badge bg-light text-dark border fs-11">{program}</span>}
                </div>
                {headline && <p className="fs-12 text-muted mb-0">{headline}</p>}
              </div>
            </div>

            <div className="fs-13 text-muted mb-2 d-flex flex-wrap gap-3">
              <span>{isFresher ? 'Fresher' : formatMonthsAsYears(totalExperienceMonths)}</span>
              {expectedCtc != null && <span>{formatCurrency(expectedCtc)}</span>}
              {location && <span>{location}</span>}
            </div>

            {(currentDesignation || currentCompany) && (
              <p className="fs-13 mb-1">
                <span className="text-muted">Current: </span>
                {[currentDesignation, currentCompany].filter(Boolean).join(' at ')}
              </p>
            )}

            {topEducations.length > 0 && (
              <p className="fs-13 mb-1">
                <span className="text-muted">Education: </span>
                {topEducations.join('; ')}
              </p>
            )}

            {preferredLocations.length > 0 && (
              <p className="fs-13 mb-1">
                <span className="text-muted">Pref. locations: </span>
                {preferredLocations.join(', ')}
              </p>
            )}

            {noticePeriodDays != null && (
              <p className="fs-13 mb-2">
                <span className="text-muted">Notice period: </span>
                {noticePeriodDays} days
              </p>
            )}

            {topSkills.length > 0 && (
              <div className="mb-2">
                <span className="fs-12 text-muted d-block mb-1">Key skills</span>
                <div className="d-flex flex-wrap gap-1">
                  {topSkills.map((skill) =>
                    matchedSet.has(skill) ? (
                      <span key={skill} className="badge bg-warning-transparent fw-semibold">
                        {skill}
                      </span>
                    ) : (
                      <span key={skill} className="badge bg-light text-dark border">
                        {skill}
                      </span>
                    ),
                  )}
                </div>
              </div>
            )}

            {profileCompletionPercent != null && (
              <div className="mb-1">
                <CompletionBar percent={profileCompletionPercent} label="Profile completeness" />
              </div>
            )}
          </div>

          <div
            className="d-flex flex-row flex-lg-column align-items-center text-center gap-2 border-start ps-lg-3"
            style={{ minWidth: 160, maxWidth: 200 }}
          >
            <Avatar name={name} size="lg" imageUrl={photoUrl} />
            {hasResume && <span className="badge bg-success-transparent">CV attached</span>}
            {extra}
            {onViewDetail && (
              <button
                type="button"
                className="btn btn-primary btn-sm waves-effect waves-light w-100"
                onClick={onViewDetail}
              >
                View Profile
              </button>
            )}
            {actions}
          </div>

          {onScheduleReminder && (
            <div className="d-flex flex-lg-column gap-2 border-start ps-lg-2">
              <button
                type="button"
                className="btn btn-icon btn-sm btn-light border-0"
                title="Set reminder"
                aria-label={`Set reminder for ${name}`}
                onClick={onScheduleReminder}
              >
                <i className="ri-time-line" />
              </button>
            </div>
          )}
        </div>

        {updatedAt && <p className="fs-11 text-muted mb-0 mt-2 border-top pt-2">Updated {formatRelativeTime(updatedAt)}</p>}
      </div>
    </div>
  )
}
